import React, { FC, useEffect } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import MissingPhoneInput from './MissingPhoneInput';
import MissingPasswordInput from './MissingPasswordInput';
import useRecipientDetails from '@/components/Common/hooks/useRecipientDetails';
import appConfig from '@/config/env';

const policyStepNameSpace = 'uploadFiles.policyStep';

export interface MissingDetailsItemProps {
    email: string;
    showPhones: boolean;
    showPasswords: boolean;
    onDetailsComplete?: () => void;
    alwaysShowInput?: boolean;
    alwaysShowPhoneInput?: boolean;
    alwaysShowPasswordInput?: boolean;
    className?: string;
    inputsClassName?: string;
    emailClassName?: string;
    passwordBlockClassName?: string;
    renderLayout?: (inputs: React.ReactNode, emailLabel: React.ReactNode) => React.ReactNode;
}

const MissingDetailsItem: FC<MissingDetailsItemProps> = observer(({
    email,
    showPhones,
    showPasswords,
    onDetailsComplete,
    alwaysShowInput,
    alwaysShowPhoneInput,
    alwaysShowPasswordInput,
    className,
    inputsClassName,
    emailClassName,
    passwordBlockClassName,
    renderLayout,
}) => {
    const { t } = useTranslation();
    const {
        currentPhone,
        passwordInfo,
        isPasswordSaving,
        phoneSaved,
        passwordSaved,
        onPhoneSubmit,
        onPasswordSave,
    } = useRecipientDetails({ email, showPhones, showPasswords });

    const hasExistingPhone = !!(currentPhone?.phone);
    const hasExistingPassword = !!passwordInfo?.hasPassword;

    useEffect(() => {
        if (!onDetailsComplete) return;
        const phoneOk = !showPhones || phoneSaved || hasExistingPhone;
        const passwordOk = !showPasswords || passwordSaved || hasExistingPassword;
        if (phoneOk && passwordOk) {
            onDetailsComplete();
        }
    }, [phoneSaved, passwordSaved, hasExistingPhone, hasExistingPassword, showPhones, showPasswords]);

    const phoneInput = showPhones ? (
        <MissingPhoneInput
            phone={currentPhone?.phone}
            prefix={currentPhone?.prefix}
            onSubmit={onPhoneSubmit}
            alwaysShowInput={alwaysShowPhoneInput ?? alwaysShowInput}
        />
    ) : null;

    const passwordHintText = appConfig.USE_SIMPLIFIED_PASSWORD_PROMPT
        ? t('recipientPage.OTPSteps.simplifiedPasswordPrompt')
        : t(`${policyStepNameSpace}.socialIdHint`);

    const passwordInput = showPasswords ? (
        <div className={passwordBlockClassName}>
            <MissingPasswordInput
                maskedPassword={passwordInfo?.maskedPassword}
                onSave={onPasswordSave}
                isLoading={isPasswordSaving}
                hintText={passwordHintText}
                alwaysShowInput={alwaysShowPasswordInput ?? alwaysShowInput}
            />
        </div>
    ) : null;

    const emailNode = <span className={emailClassName}>{email}</span>;

    if (renderLayout) {
        const inputs = inputsClassName ? (
            <div className={inputsClassName}>
                {phoneInput}
                {passwordInput}
            </div>
        ) : (
            <>
                {phoneInput}
                {passwordInput}
            </>
        );
        return <>{renderLayout(inputs, emailNode)}</>;
    }

    return (
        <div className={className}>
            <div className={inputsClassName}>
                {phoneInput}
                {emailNode}
                {passwordInput}
            </div>
        </div>
    );
});

export default MissingDetailsItem;
